import classNames from 'classnames';
import { Route } from 'next';
import Link from 'next/link';
import { CSSProperties, ReactNode } from 'react';

export interface PrimaryButtonProps {
  variant?: 'outlined' | 'secondary' | 'cta' | 'ghost' | 'nav' | 'radial';
  slim?: boolean;
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
  children: ReactNode;
  href?: Route;
  linkClassName?: string;
  submit?: boolean;
}

const ButtonBase = ({
  as,
  href,
  submit,
  className,
  onClick,
  children,
  ...props
}: {
  as: 'button' | 'a';
  href?: Route;
  submit?: boolean;
  className: string;
  style?: CSSProperties;
  onClick?: () => void;
  children: ReactNode;
}) => {
  if (as === 'a' && href) {
    // `mailto:` and `tel:` are handed to the operating system, not routed.
    // next/link would call router.push on them, and pushing a scheme the
    // router cannot resolve is how these end up doing nothing at all.
    if (/^[a-z][a-z0-9+.-]*:/i.test(href)) {
      return (
        <a href={href} className={className} onClick={onClick} {...props}>
          {children}
        </a>
      );
    }

    return (
      <Link href={href} className={className} onClick={onClick} {...props}>
        {children}
      </Link>
    );
  }

  return (
    <button
      type={submit ? 'submit' : 'button'}
      className={className}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

const Button = ({
  href,
  variant,
  slim,
  className,
  style,
  linkClassName,
  submit,
  children,
  onClick,
}: PrimaryButtonProps) => {
  const buttonClass = classNames(
    'inline-flex items-center font-medium py-1.5 px-4 rounded-[0.25rem] rounded-br-[1.5rem] text-nowrap cursor-pointer',
    {
      'text-outline-fill border border-[rgba(0,0,163,0.32)] bg-white/95 text-[#0000A3] text-base leading-6 tracking-[-0.2px] shadow-sm hover:bg-outline-hover':
        variant === 'outlined',
      'bg-ocean-blue-500 text-white-13a shadow-sm !py-3 hover:bg-primary-hover':
        !variant,
      // toggle for lighthouse report - enhanced contrast
      // 'bg-primary-default text-white-contrast hover:bg-primary-hover': !variant,
      'bg-white-13a text-primary-10 hover:bg-outline-hover':
        variant === 'secondary',
      'text-outline-fill border border-outline-stroke !px-10 !py-4 text-sm text-primary-10 hover:bg-outline-hover':
        variant === 'cta',
      'text-primary-9': variant === 'ghost',
      // The nav pill's fill is a radial gradient anchored in its bottom-right
      // corner, not the flat vertical ramp this used to carry. Geometry comes
      // from the Figma button (node 10186:17618): an ellipse 100% of the
      // button's width by 232.7% of its height, so only the bright inner stops
      // land inside the pill and the rest reads as Ocean Blue 400.
      'bg-[radial-gradient(100%_232.7%_at_100%_100%,#89D9F7_0%,#74B0E7_16.95%,#5E86D6_33.9%,#495DC6_50.85%,#3333B5_67.79%)] text-white-13a !h-7 !rounded-full !px-2 !py-0.5 text-[14px] shadow-[0_1px_2px_0_rgba(20,21,26,0.05),inset_0_-1px_1px_0_rgba(0,0,0,0.1)] hover:brightness-110':
        variant === 'nav',
      'bg-[radial-gradient(95.21%_62.17%_at_50%_0%,#89D9F7_0%,#3576CD_39.4%,#4D4DBF_61.91%,#0000A3_100%)] border-x-[1.4px] border-b-[1.4px] border-t-0 border-solid border-[rgba(0,0,163,0.16)] text-white-13a shadow-sm !py-3 hover:brightness-110':
        variant === 'radial',
      'min-h-10': slim && variant !== 'nav',
      'min-h-12': !slim && variant !== 'nav',
    },
    className,
    linkClassName
  );

  return (
    <ButtonBase
      as={href ? 'a' : 'button'}
      href={href}
      className={buttonClass}
      style={style}
      onClick={onClick}
      submit={submit}
    >
      {children}
    </ButtonBase>
  );
};

export default Button;
